import React from 'react';
import { Hand, MessagesSquare, Timer, Users } from 'lucide-react';

const OPCIONES = [
  {
    valor: 'callar',
    titulo: 'Se calla',
    detalle: 'Deja de responder hasta que un vendedor escriba, o hasta que le devuelvas la conversación desde la ficha del contacto.',
    Icono: Hand
  },
  {
    valor: 'seguir',
    titulo: 'Sigue atendiendo',
    detalle: 'Contesta mientras llega el vendedor. No vuelve a escalar ni a avisar por la misma conversación.',
    Icono: MessagesSquare
  }
];

const aEntero = (texto, min, max) => {
  const n = parseInt(texto, 10);
  if (Number.isNaN(n)) return min;
  return Math.min(max, Math.max(min, n));
};

/**
 * Qué pasa cuando la IA le pasa una conversación a una persona: si la IA se
 * calla o sigue, y cuánto espera cada vendedor antes de pasársela a otro.
 *
 * El tope de reasignaciones existe para que un lead que nadie atiende no gire
 * para siempre entre vendedores: al agotarse se avisa a los managers una vez.
 */
const TrasEscalar = ({ value, onChange }) => {
  const modo = value.tras_escalar ?? 'callar';
  const minutos = value.reassignment_timeout_minutes ?? 15;
  const tope = value.max_reasignaciones ?? 2;

  const cambiar = (campo, valor) => onChange({ ...value, [campo]: valor });

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <label className="text-xs font-black text-slate-500 dark:text-gray-400 uppercase tracking-widest">
          Después de escalar, la IA…
        </label>
        <div role="radiogroup" aria-label="Qué hace la IA después de escalar" className="grid gap-2">
          {OPCIONES.map(({ valor, titulo, detalle, Icono }) => {
            const activa = modo === valor;
            return (
              <button
                key={valor}
                type="button"
                role="radio"
                aria-checked={activa}
                onClick={() => cambiar('tras_escalar', valor)}
                className={`text-left flex gap-3 p-3.5 rounded-2xl border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 ${activa
                  ? 'border-primary/40 bg-primary/[0.06]'
                  : 'border-slate-100 dark:border-white/5 bg-slate-50 dark:bg-white/5 hover:border-slate-200 dark:hover:border-white/10'}`}
              >
                <span className={`mt-0.5 shrink-0 size-7 rounded-lg flex items-center justify-center ${activa
                  ? 'bg-primary text-white'
                  : 'bg-white dark:bg-white/10 text-slate-400'}`}>
                  <Icono size={15} />
                </span>
                <span className="min-w-0">
                  <span className={`block text-sm font-bold ${activa ? 'text-slate-900 dark:text-white' : 'text-slate-700 dark:text-gray-300'}`}>
                    {titulo}
                  </span>
                  <span className="block text-[11px] leading-snug text-slate-500 dark:text-gray-400 mt-0.5">{detalle}</span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-3">
        <label className="text-xs font-black text-slate-500 dark:text-gray-400 uppercase tracking-widest flex items-center gap-2">
          <Timer size={14} /> Si el vendedor no responde
        </label>
        <div className="bg-slate-50 dark:bg-white/5 p-4 rounded-2xl border border-slate-100 dark:border-white/5 space-y-3">
          <div className="flex items-center gap-3">
            <input
              type="number"
              min="1"
              max="1440"
              value={minutos}
              onChange={(e) => cambiar('reassignment_timeout_minutes', aEntero(e.target.value, 1, 1440))}
              aria-label="Minutos antes de reasignar"
              className="w-16 bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg px-2 py-1.5 text-lg font-black text-slate-900 dark:text-white text-center tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
            <p className="text-xs font-bold text-slate-700 dark:text-gray-200">minutos sin respuesta, pasa a otro vendedor</p>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="number"
              min="0"
              max="10"
              value={tope}
              onChange={(e) => cambiar('max_reasignaciones', aEntero(e.target.value, 0, 10))}
              aria-label="Máximo de reasignaciones"
              className="w-16 bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg px-2 py-1.5 text-lg font-black text-slate-900 dark:text-white text-center tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
            <p className="text-xs font-bold text-slate-700 dark:text-gray-200">
              {tope === 0 ? 'no se reasigna nunca' : tope === 1 ? 'vez como máximo' : 'veces como máximo'}
            </p>
          </div>
        </div>
        <p className="text-[11px] text-slate-400 dark:text-gray-500 leading-relaxed flex gap-1.5">
          <Users size={12} className="shrink-0 mt-0.5" />
          <span>
            Respeta las reglas de asignación y nunca repite a un vendedor.
            {tope === 0
              ? ' Si el primero no responde, se avisa a los managers.'
              : ' Si nadie responde, se avisa a los managers una sola vez y el lead deja de rotar.'}
          </span>
        </p>
      </div>
    </div>
  );
};

export default TrasEscalar;
